import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

function ImageUpload({
  uploadAttachment,
  image,
  isLoading,
  deleteAttachment,
  readOnly,
  showError,
  onRetry,
  onView,
  onDownload,
}) {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleFileChange = (event) => {
    if (readOnly) return;
    const file = event.target.files[0];
    uploadAttachment(file);
  };

  const handleDragOver = (event) => {
    if (readOnly) return;
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    if (readOnly) return;
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    if (readOnly) return;
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    uploadAttachment(file);
  };

  return (
    <span
      className="OnePortionFlex imageUploadWrapper"
      style={{ alignSelf: 'center', textAlign: 'center' }}
    >
      <input
        type="file"
        id="fileInput"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        disabled={readOnly}
      />
      <span
        className={`${isDragging ? 'dragging' : ''}`}
        style={{ position: 'relative', display: 'inline-block' }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => {
          if (showError || isLoading || image || readOnly) return;
          fileInputRef.current.click();
        }}
      >
        {showError ? (
          <div className="error-message">
            {showError}
            {onRetry ? (
              <button
                type="button"
                className="buttonClone"
                style={{ display: 'block', margin: '8px auto 0' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry();
                }}
              >
                Retry
              </button>
            ) : null}
          </div>
        ) : isLoading ? (
          <img
            src="/Spinner-2.9s-297px.gif"
            className="loadingIcon icon"
            style={{ height: '40px', width: '40px' }}
            alt="Loading..."
          />
        ) : image ? (
          <img
            src={image}
            alt="Shot Image"
            className="loading-box shotImage"
            style={{ display: 'block' }}
          />
        ) : (
          <div className="uploadPlaceholder">
            <span className="uploadPlaceholderBadge">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="4" width="14" height="12" rx="2" stroke="#7A64EB" strokeWidth="1.6" />
                <circle cx="6.2" cy="8.2" r="1.3" fill="#7A64EB" />
                <path
                  d="M3 14.5 6.6 11a1 1 0 0 1 1.4 0L11.5 14.5"
                  stroke="#7A64EB"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="18" cy="16" r="5.5" fill="#7A64EB" />
                <path
                  d="M18 18.6v-5.2M15.3 15.7 18 13l2.7 2.7"
                  stroke="#fff"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="uploadPlaceholderTitle">Click to upload an image</span>
            <span className="uploadPlaceholderSubtitle">or drag &amp; drop it here</span>
            <span className="uploadPlaceholderHint">JPG, JPEG or PNG</span>
          </div>
        )}

        {image && !showError && !isLoading ? (
          // Hidden until hover on desktop; always visible on touch (see baseElements.css)
          <div
            className="shotImageActionBar"
            role="toolbar"
            aria-label="Image actions"
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              aria-label="View image"
              size="medium"
              className="shotImageActionButton view"
              onClick={() => onView?.()}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="Download image"
              size="medium"
              className="shotImageActionButton"
              onClick={() => onDownload?.()}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
            {!readOnly ? (
              <IconButton
                aria-label="Delete image"
                size="medium"
                className="shotImageActionButton delete"
                onClick={() => setConfirmDeleteOpen(true)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            ) : null}
          </div>
        ) : null}
      </span>

      {/* Rendered outside the clickable span: portal clicks still bubble through React */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        aria-labelledby="delete-image-dialog-title"
      >
        <DialogTitle id="delete-image-dialog-title">Delete this image?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the image from the shot. Generated images can&apos;t be restored once
            deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmDeleteOpen(false);
              deleteAttachment();
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </span>
  );
}

export default ImageUpload;
